import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core'
import { AbstractControl, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms'
import { InfoboothsFacade } from '@galabid/gb-dashboard/setup/data-access-infobooths'
import {
  MENU_ITEMS_LOADER,
  MenuItemsEntity,
  MenuItemsFacade,
  MenuItemPageFrontendRouteEnum,
} from '@galabid/gb-dashboard/setup/data-access-menu-items'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { CategoriesFacade } from 'gb-dashboard/item/data-access-categories'
import { WidgetPagesFacade } from 'libs/gb-dashboard/branding/data-access/src/lib/+state/widget-pages.facade'
import * as MenuItemsActions from 'libs/gb-dashboard/setup/data-access-menu-items/src/lib/+state/menu-items.actions'
import { MenuItemsButtonTypesEnum } from 'libs/gb-dashboard/setup/data-access-menu-items/src/lib/+state/menu-items.models'
import { PendingChangesService } from 'libs/gb-dashboard/shared/utils/src/lib/gb-pending-changes/gb-pending-changes.service'
import { GbFormService } from 'libs/shared/utils/services/src/lib/gb-form-service/gb-form.service'
import { Observable, combineLatest, map, take } from 'rxjs'
import { SelectedItemFacade } from 'libs/gb-dashboard/item/data-access-selected-item/src/lib/+state/selected-item.facade'
import * as SelectedItemActions from 'libs/gb-dashboard/item/data-access-selected-item/src/lib/+state/selected-item.actions'
import {
  BiddingTypeEnum,
  ItemTypeEnum,
  SlimItemsEntity,
} from 'libs/gb-dashboard/item/data-access/src/lib/+state/items.models'
import { APP_CONFIG } from 'libs/shared/app-config/src/lib/gb-app-config'
import { EnvironmentModel } from 'apps/gb-dashboard/src/environments/environment.model'
import { Inject } from '@angular/core'
import { Clipboard } from '@angular/cdk/clipboard'
import { GbNotificationService } from 'libs/shared/utils/notification/src/lib/service/gb-notification.service'
import { fadeUpAnimation } from '@galabid/shared/utils/animations'
import { TranslateService } from '@ngx-translate/core'
import { SelectedAuctionFacade } from 'libs/gb-dashboard/auctions/data-access-selected-auction/src/lib/+state/selected-auction.facade'

export interface ButtonTypeOption {
  display: string
  value: MenuItemsButtonTypesEnum
  shouldShow: boolean
}

@UntilDestroy()
@Component({
  selector: 'gb-menu-edit',
  templateUrl: './gb-menu-edit.component.html',
  styleUrls: ['./gb-menu-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [fadeUpAnimation],
  standalone: false,
})
export class GbMenuEditComponent implements OnInit, OnDestroy {
  editCreateLoaded$ = this.menuItemsFacade.editCreateLoaded$
  MenuItemsButtonTypesEnum = MenuItemsButtonTypesEnum
  allInfobooths$ = this.infoboothsFacade.allInfobooths$

  allHubs$ = this.widgetPagesFacade.allWidgetPages$
  allOldWidgets$ = this.widgetPagesFacade.allOldWidgets$
  allCategories$ = this.categoriesFacade.allCategories$

  selectedMenuItem$ = this.menuItemsFacade.selectedMenuItem$
  selectedItem$ = this.selectedItemFacade.selectedItem$

  isCreationMode$: Observable<boolean> = this.selectedMenuItem$.pipe(map((selectedMenuItem) => !selectedMenuItem))

  get isAccountType() {
    return this.button_type?.value === MenuItemsButtonTypesEnum.MyAccount
  }

  menuPageLink = ''

  buttonTypeOptions$ = combineLatest([
    this.selectedAuctionFacade.auction$,
    this.allInfobooths$,
    this.allHubs$,
    this.allOldWidgets$,
    this.allCategories$,
  ]).pipe(
    map(([selectedAuction, infobooths, hubs, oldWidgets, categories]) => {
      const allOptions: ButtonTypeOption[] = [
        {
          display: 'SETTINGS.MENU.BUTTON_TYPE_LABEL.INFOBOOTH',
          value: MenuItemsButtonTypesEnum.Infobooth,
          shouldShow: infobooths && infobooths.length > 0,
        },
        {
          display: 'SETTINGS.MENU.BUTTON_TYPE_LABEL.EXTERNAL_LINK',
          value: MenuItemsButtonTypesEnum.ExternalLink,
          shouldShow: true,
        },
        {
          display: 'SETTINGS.MENU.BUTTON_TYPE_LABEL.COMMENT_FEED',
          value: MenuItemsButtonTypesEnum.ActivityFeed,
          shouldShow: true,
        },
        {
          display: 'SETTINGS.MENU.BUTTON_TYPE_LABEL.HUB',
          value: MenuItemsButtonTypesEnum.Hub,
          shouldShow: oldWidgets && oldWidgets.length > 0,
        },
        {
          display: 'SETTINGS.MENU.BUTTON_TYPE_LABEL.WIDGET_PAGE',
          value: MenuItemsButtonTypesEnum.WidgetPage,
          shouldShow: hubs && hubs.length > 0,
        },
        {
          display: 'SETTINGS.MENU.BUTTON_TYPE_LABEL.CATEGORY',
          value: MenuItemsButtonTypesEnum.Category,
          shouldShow: categories && categories.length > 0,
        },
        {
          display: 'SETTINGS.MENU.BUTTON_TYPE_LABEL.ITEM',
          value: MenuItemsButtonTypesEnum.Item,
          shouldShow: !!selectedAuction && selectedAuction.item_count > 0,
        },
        {
          display: 'SETTINGS.MENU.BUTTON_TYPE_LABEL.CONTACT_HOST',
          value: MenuItemsButtonTypesEnum.ContactHost,
          shouldShow: true,
        },
        {
          display: 'SETTINGS.MENU.BUTTON_TYPE_LABEL.FAVOURITE_ITEMS',
          value: MenuItemsButtonTypesEnum.FavouriteItems,
          shouldShow: true,
        },
        {
          display: 'SETTINGS.MENU.BUTTON_TYPE_LABEL.DONATE_ITEM',
          value: MenuItemsButtonTypesEnum.DonateItem,
          shouldShow: !!selectedAuction?.donate_item_enabled,
        },
      ]

      return allOptions.filter((option) => option.shouldShow)
    }),
  )

  editMenuOptionFormFields = {
    button_type: new FormControl('', [Validators.required]),
    category: new FormControl(''),
    external_link: new FormControl(true),
    widget_page: new FormControl(''),
    hub: new FormControl(''),
    icon: new FormControl('star', [Validators.required]),
    infobooth: new FormControl(''),
    is_visible: new FormControl(true),
    item: new FormControl(),
    item_type: new FormControl(''),
    label: new FormControl('', [Validators.required]),
    order: new FormControl(0),
    url: new FormControl(''),
  }

  editMenuOptionForm = new FormGroup(this.editMenuOptionFormFields)

  get button_type() {
    return this.editMenuOptionForm.get('button_type')
  }

  get category() {
    return this.editMenuOptionForm.get('category')
  }

  get external_link() {
    return this.editMenuOptionForm.get('external_link') as AbstractControl as FormControl
  }

  get hub() {
    return this.editMenuOptionForm.get('hub')
  }

  get widget_page() {
    return this.editMenuOptionForm.get('widget_page')
  }

  get icon() {
    return this.editMenuOptionForm.get('icon')
  }

  get infobooth() {
    return this.editMenuOptionForm.get('infobooth')
  }

  get is_visible() {
    return this.editMenuOptionForm.get('is_visible') as AbstractControl as FormControl
  }

  get item() {
    return this.editMenuOptionForm.get('item') as FormControl
  }

  get item_type() {
    return this.editMenuOptionForm.get('item_type')
  }

  get label() {
    return this.editMenuOptionForm.get('label')
  }

  get order() {
    return this.editMenuOptionForm.get('order')
  }

  get url() {
    return this.editMenuOptionForm.get('url')
  }

  categoriesLimit = 5
  categoriesOffset = 0

  itemsLimit = 5
  itemsOffset = 0
  itemsSearchTerm = ''

  itemTypeMap = new Map<string, string>([
    [ItemTypeEnum.Bidding, 'ITEMS.TYPE.SILENT'],
    [BiddingTypeEnum.Live, 'ITEMS.TYPE.LIVE'],
    [BiddingTypeEnum.Sealed, 'ITEMS.TYPE.SEALED'],
    [ItemTypeEnum.Donation, 'ITEMS.TYPE.DONATION'],
    [ItemTypeEnum.Purchase, 'ITEMS.TYPE.PURCHASE'],
    [ItemTypeEnum.Raffle, 'ITEMS.TYPE.RAFFLE'],
  ])

  constructor(
    private menuItemsFacade: MenuItemsFacade,
    private infoboothsFacade: InfoboothsFacade,
    private gbFormService: GbFormService,
    private categoriesFacade: CategoriesFacade,
    private selectedItemFacade: SelectedItemFacade,
    private widgetPagesFacade: WidgetPagesFacade,
    private pendingChangesService: PendingChangesService,
    private selectedAuctionFacade: SelectedAuctionFacade,
    private clipboard: Clipboard,
    private gbNotificationService: GbNotificationService,
    private translateService: TranslateService,
    @Inject(APP_CONFIG) protected appConfig: EnvironmentModel,
  ) {}

  ngOnInit(): void {
    this.menuItemsFacade.dispatch(MenuItemsActions.loadMenuItemResources())

    this.setupForm()
    this.watchFormChanges()

    this.pendingChangesService.registerPendingChanges(this.editMenuOptionForm, this.selectedMenuItem$)
  }

  ngOnDestroy(): void {
    this.menuItemsFacade.dispatch(MenuItemsActions.clearSelectMenuItem())
    this.pendingChangesService.resetPendingChanges()
  }

  closeDialog() {
    this.pendingChangesService.closeDialogPendingChanges(GbMenuEditComponent)
  }

  setItem(item: SlimItemsEntity) {
    this.label?.setValue(item.name)
  }

  clearItem() {
    this.label?.setValue('')
  }

  setupForm() {
    this.selectedMenuItem$.pipe(take(1)).subscribe((selectedMenuItem) => {
      if (selectedMenuItem) {
        this.gbFormService.patchValues(selectedMenuItem, this.editMenuOptionForm)
        this.button_type?.disable()
      } else {
        this.button_type?.enable()
      }

      if (selectedMenuItem?.button_type === MenuItemsButtonTypesEnum.Item && selectedMenuItem.item) {
        this.syncSelectedItem(selectedMenuItem.item)
      }

      this.updateMenuPageLink()
    })
  }

  clearLabelAndOthers() {
    this.label?.reset()
    this.hub?.reset()
    this.category?.reset()
    this.item?.reset()
    this.url?.reset()
    this.infobooth?.reset()
    this.widget_page?.reset()
  }

  onButtonTypeSelected(selectedOption: ButtonTypeOption) {
    const value = selectedOption.value
    if (value) this.icon?.setValue(this.getDefaultIcon(value))
    this.clearLabelAndOthers()
    const controls = ['infobooth', 'hub', 'category', 'item', 'url']
    controls.forEach((controlName) => {
      const control = this.editMenuOptionForm.get(controlName)
      if (control) {
        control.clearValidators()
        control.updateValueAndValidity()
      }
    })

    switch (value) {
      case MenuItemsButtonTypesEnum.Infobooth:
        this.setValidator('infobooth', [Validators.required])
        break

      case MenuItemsButtonTypesEnum.WidgetPage:
        this.setValidator('widget_page', [Validators.required])
        break

      case MenuItemsButtonTypesEnum.Category:
        this.setValidator('category', [Validators.required])
        break

      case MenuItemsButtonTypesEnum.Item:
        this.setValidator('item', [Validators.required])
        break

      case MenuItemsButtonTypesEnum.ExternalLink:
        this.setValidator('url', [Validators.required])
        break
    }
  }

  watchFormChanges() {
    this.editMenuOptionForm.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.updateMenuPageLink()
    })
  }

  updateMenuPageLink() {
    this.selectedAuctionFacade.urlName$.pipe(take(1)).subscribe((urlName) => {
      const formValues = this.editMenuOptionForm.value
      const partialMenuItem: Partial<MenuItemsEntity> = {
        button_type: this.button_type?.value || '',
        infobooth: formValues.infobooth,
        category: formValues.category,
        item: formValues.item?.uuid || formValues.item,
        item_type: formValues.item_type,
        hub: formValues.hub,
        widget_page: formValues.widget_page,
      }

      const menuPageLink = this.getMenuPageLink(urlName, partialMenuItem)
      this.menuPageLink = menuPageLink ? this.appConfig.frontendUrl + menuPageLink : ''
    })
  }

  syncSelectedItem(uuid: string) {
    this.selectedItemFacade.dispatch(SelectedItemActions.syncSelectedItem({ uuid }))
  }

  getDefaultIcon(buttonType: string): string {
    switch (buttonType) {
      case MenuItemsButtonTypesEnum.Infobooth:
        return 'info'

      case MenuItemsButtonTypesEnum.ExternalLink:
        return 'open_in_new'

      case MenuItemsButtonTypesEnum.ActivityFeed:
        return 'rss_feed'

      case MenuItemsButtonTypesEnum.WidgetPage:
        return 'home'

      case MenuItemsButtonTypesEnum.SearchAndCategories:
        return 'search'

      case MenuItemsButtonTypesEnum.Category:
        return 'category'

      case MenuItemsButtonTypesEnum.Item:
        return 'label'

      case MenuItemsButtonTypesEnum.ItemType:
        return 'label'

      case MenuItemsButtonTypesEnum.ContactHost:
        return 'email'

      case MenuItemsButtonTypesEnum.FavouriteItems:
        return 'favorite'

      case MenuItemsButtonTypesEnum.AllItems:
        return 'list'

      case MenuItemsButtonTypesEnum.MyAccount:
        return 'account_circle'

      case MenuItemsButtonTypesEnum.DonateItem:
        return 'volunteer_activism'
    }
    return 'star'
  }

  setValidator(controlName: string, validators: ValidatorFn[]) {
    const control = this.editMenuOptionForm.get(controlName)
    if (control) {
      control.setValidators(validators)
      control.updateValueAndValidity()
    }
  }

  onIconSelect(icon: string) {
    this.icon?.setValue(icon)
  }

  submitForm(isCreationMode: boolean) {
    this.editMenuOptionForm.valid ? this.submitData(isCreationMode) : this.onError()
  }

  onError() {
    this.editMenuOptionForm.markAllAsTouched()
  }

  submitData(isCreationMode: boolean) {
    isCreationMode
      ? this.menuItemsFacade.dispatch(
          MenuItemsActions.createMenuItem({
            menuItem: this.getMenuItemPayload(),
          }),
        )
      : this.menuItemsFacade.dispatch(
          MenuItemsActions.updateMenuItem({
            menuItem: this.getMenuItemPayload(),
          }),
        )
  }

  getMenuItemPayload(): Partial<MenuItemsEntity> {
    const formValues = this.editMenuOptionForm.value

    const payload: Partial<MenuItemsEntity> = {
      button_type: this.button_type?.value || '',
      label: formValues.label || '',
      is_visible: !!formValues.is_visible,
      item: formValues.item?.uuid || null,
      infobooth: formValues.infobooth || null,
      url: formValues.url || '',
      external_link: formValues.external_link,
      widget_page: formValues.widget_page || null,
      icon: formValues.icon,
      category: formValues.category || null,
      item_type: formValues.item_type,
      hub: formValues.hub || null,
    }

    return payload
  }

  protected readonly MENU_ITEMS_LOADER = MENU_ITEMS_LOADER

  getMenuPageLink(urlName: string, menuItem: Partial<MenuItemsEntity>): string {
    if (!menuItem.button_type) {
      return ''
    }

    switch (menuItem.button_type) {
      case MenuItemsButtonTypesEnum.Infobooth:
        return menuItem.infobooth ? `${urlName}/${MenuItemPageFrontendRouteEnum.INFO}/${menuItem.infobooth}` : ''

      case MenuItemsButtonTypesEnum.MyAccount:
        return `${urlName}/${MenuItemPageFrontendRouteEnum.ACCOUNT}`

      case MenuItemsButtonTypesEnum.SearchAndCategories:
        return `${urlName}/${MenuItemPageFrontendRouteEnum.CATEGORIES}`

      case MenuItemsButtonTypesEnum.AllItems:
        return `${urlName}/${MenuItemPageFrontendRouteEnum.ITEMS}`

      case MenuItemsButtonTypesEnum.FavouriteItems:
        return `${urlName}/${MenuItemPageFrontendRouteEnum.FAVOURITES}`

      case MenuItemsButtonTypesEnum.Item:
        return menuItem.item ? `${urlName}/${MenuItemPageFrontendRouteEnum.ITEMS}/${menuItem.item}` : ''

      case MenuItemsButtonTypesEnum.Ticketing:
        return `${urlName}/${MenuItemPageFrontendRouteEnum.EVENT_TICKETS}`

      case MenuItemsButtonTypesEnum.Category:
        return menuItem.category ? `${urlName}/${MenuItemPageFrontendRouteEnum.CATEGORIES}/${menuItem.category}` : ''

      case MenuItemsButtonTypesEnum.ItemType:
        return menuItem.item_type
          ? `${urlName}/${MenuItemPageFrontendRouteEnum.ITEMS}?item_type=${menuItem.item_type}`
          : ''

      case MenuItemsButtonTypesEnum.Hub:
        return menuItem.hub ? `${urlName}/${MenuItemPageFrontendRouteEnum.WIDGETS}/${menuItem.hub}` : ''

      case MenuItemsButtonTypesEnum.WidgetPage:
        return menuItem.widget_page ? `${urlName}/${MenuItemPageFrontendRouteEnum.WIDGETS}/${menuItem.widget_page}` : ''

      case MenuItemsButtonTypesEnum.DonateItem:
        return `${urlName}/${MenuItemPageFrontendRouteEnum.DONATE_ITEM}`

      default:
        return ''
    }
  }

  openUrl(url: string): void {
    window.open(url, '_blank')
  }

  copyUrl(url: string): void {
    this.clipboard.copy(url)
    this.gbNotificationService.success('SETTINGS.MENU.LINK_COPIED')
  }

  getItemTypeTranslationKey(item_type: string | null | undefined): string | undefined {
    if (!item_type) return
    const key = this.itemTypeMap.get(item_type) ?? item_type
    return this.translateService.instant(key)
  }
}
